import type { PublicUser } from '@fitness/api-client';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/Button';
import { PageHeader } from '@/components/PageHeader';
import { UserList } from '@/components/community/UserRow';
import { describeError } from '@/lib/api';
import { useMyBlocks, useToggleBlock } from '@/lib/queries/users';

export const Route = createFileRoute('/_app/settings/blocks')({
  component: BlocksPage,
});

/** Cuentas bloqueadas, con el desbloqueo en la propia fila. */
function BlocksPage() {
  const query = useMyBlocks();
  const users = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title="Cuentas bloqueadas"
        subtitle="No podéis veros ni interactuar."
        action={
          <Link
            to="/settings"
            aria-label="Volver a ajustes"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <UserList
        users={users}
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        errorTitle="No hemos podido cargar la lista"
        loadingLabel="Cargando cuentas bloqueadas…"
        empty={
          <p className="rounded-2xl border border-dashed border-line bg-surface-raised px-5 py-10 text-center text-sm text-ink-muted">
            No has bloqueado a nadie.
          </p>
        }
        renderAction={(user) => <UnblockAction user={user} />}
      />

      <p className="mt-4 text-xs text-ink-faint">
        Desbloquear no restaura los seguimientos que había antes del bloqueo: hay que volver a
        seguirse a mano.
      </p>
    </>
  );
}

function UnblockAction({ user }: { user: PublicUser }) {
  const [confirming, setConfirming] = useState(false);
  const block = useToggleBlock();

  if (!confirming) {
    return (
      <Button variant="secondary" onClick={() => setConfirming(true)}>
        Desbloquear
      </Button>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex gap-2">
        <Button
          variant="danger"
          loading={block.isPending}
          onClick={() => block.mutate({ handle: user.handle, isBlocked: true })}
        >
          Sí
        </Button>
        <Button variant="secondary" disabled={block.isPending} onClick={() => setConfirming(false)}>
          No
        </Button>
      </div>
      {block.isError ? (
        <p role="alert" className="text-xs text-danger">
          {describeError(block.error)}
        </p>
      ) : null}
    </div>
  );
}
